const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',')

const KEY_DIRECTIONS = {
    ArrowLeft: 'left',
    ArrowUp: 'up',
    ArrowRight: 'right',
    ArrowDown: 'down',
    37: 'left',
    38: 'up',
    39: 'right',
    40: 'down',
}

function center(rect) {
    return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
    }
}

function directionalDistance(from, to, direction) {
    const dx = to.x - from.x
    const dy = to.y - from.y
    if (direction === 'left' && dx >= -1) return null
    if (direction === 'right' && dx <= 1) return null
    if (direction === 'up' && dy >= -1) return null
    if (direction === 'down' && dy <= 1) return null

    const primary = direction === 'left' || direction === 'right' ? Math.abs(dx) : Math.abs(dy)
    const secondary = direction === 'left' || direction === 'right' ? Math.abs(dy) : Math.abs(dx)
    return primary + secondary * 2.5
}

export function findDirectionalTarget(currentElement, elements, direction, rectByElement = null) {
    if (!currentElement || !elements.length) return elements[0] || null
    const getRect = (element) => rectByElement?.get(element) || element.getBoundingClientRect()
    const currentRect = getRect(currentElement)
    const currentCenter = center(currentRect)
    let best = null
    let bestScore = Number.POSITIVE_INFINITY

    for (const element of elements) {
        if (element === currentElement) continue
        const rect = getRect(element)
        const score = directionalDistance(currentCenter, center(rect), direction)
        if (score === null || score >= bestScore) continue
        best = element
        bestScore = score
    }
    return best
}

function isVisible(element, browserWindow) {
    const rect = element.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return false
    const style = browserWindow.getComputedStyle(element)
    return style.display !== 'none' && style.visibility !== 'hidden'
}

export function findActiveFocusScope(documentObject, browserWindow) {
    const dialogs = Array.from(documentObject.querySelectorAll('[aria-modal="true"]'))
        .filter((element) => isVisible(element, browserWindow))
    return dialogs[dialogs.length - 1] || documentObject
}

function visibleFocusableElements(scope, browserWindow, activeElement) {
    const viewportWidth = Number(browserWindow.innerWidth || 1920)
    const viewportHeight = Number(browserWindow.innerHeight || 1080)
    const elements = []
    const rectByElement = new Map()

    for (const element of Array.from(scope.querySelectorAll(FOCUSABLE_SELECTOR))) {
        const rect = element.getBoundingClientRect()
        if (rect.width <= 0 || rect.height <= 0) continue
        const isNearViewport = rect.right >= -viewportWidth
            && rect.left <= viewportWidth * 2
            && rect.bottom >= -viewportHeight
            && rect.top <= viewportHeight * 2
        if (element !== activeElement && !isNearViewport) continue
        const style = browserWindow.getComputedStyle(element)
        if (style.display === 'none' || style.visibility === 'hidden') continue
        elements.push(element)
        rectByElement.set(element, rect)
    }
    return { elements, rectByElement }
}

function shouldKeepEditingKey(element, direction) {
    if (!element?.matches) return false
    if (element.matches('select')) return true
    if (!element.matches('input, textarea, [contenteditable]:not([contenteditable="false"])')) return false
    return direction === 'left' || direction === 'right'
}

function focusElement(element) {
    if (!element) return
    try {
        element.focus({ preventScroll: true })
    } catch (_) {
        element.focus()
    }
    try {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    } catch (_) {
        element.scrollIntoView(false)
    }
}

export function installTvNavigation(
    documentObject = typeof document === 'undefined' ? null : document,
    browserWindow = typeof window === 'undefined' ? null : window
) {
    if (!documentObject || !browserWindow) return () => {}

    const handleKeydown = (event) => {
        if (event.defaultPrevented) return
        const direction = KEY_DIRECTIONS[event.key] || KEY_DIRECTIONS[event.keyCode]
        if (!direction) return
        if (shouldKeepEditingKey(event.target, direction)) return

        const scope = findActiveFocusScope(documentObject, browserWindow)
        const activeDocumentElement = documentObject.activeElement
        const { elements, rectByElement } = visibleFocusableElements(
            scope,
            browserWindow,
            activeDocumentElement
        )
        if (!elements.length) return
        const activeElement = elements.includes(activeDocumentElement)
            ? activeDocumentElement
            : null
        const target = findDirectionalTarget(activeElement, elements, direction, rectByElement)
        if (!target) return

        event.preventDefault()
        focusElement(target)
    }

    documentObject.addEventListener('keydown', handleKeydown)
    return () => documentObject.removeEventListener('keydown', handleKeydown)
}
